import { FC, useEffect } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import useI18n, { Translation } from "../../../hooks/useTranslations";
import { BaseOrientation, getBaseOrientations } from "../../../http/border/types/bases";
import { CornerClassification, getCornerClassifications } from "../../../http/border/types/configurationBorders";
import { BorderConfigurationData } from "../../../http/border/types/configurations";
import SvgAdd from "../../icons/Add";
import SvgDelete from "../../icons/Delete";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import LoadingSpinner from "../../ui/LoadingSpinner";
import Numeric from "../../ui/Numeric";
import { CustomSelect } from "../../ui/select/CustomSelect";
import Switch from "../../ui/Switch";
import styles from "./BorderManualMeasurementsStep.module.css";

export type BorderManualMeasurementsFormBody = {
  isClosedPerimeter: boolean;
  segments: {
    length: number | null;
    orientation: BaseOrientation | null;
    cornerClassification: CornerClassification | null;
  }[];
};

const MILLIMETERS_PER_METER = 1000;

const CLOSED_PERIMETER_MIN_SEGMENTS = 4;

// borderData.segments[].length is persisted/API-bound in millimeters (the
// `length int` schema column, matching the Rail.Length/span millimeter
// convention used elsewhere); this form displays and accepts meters instead,
// so the user types "10.5" rather than the raw "10500". No existing mm<->m
// conversion utility was found elsewhere in the codebase to reuse, so both
// directions are defined here, next to the one place (toFormBody) that
// currently needs the mm->m direction -- metersToMillimeters is exported for
// the parent (BorderPerimeterStep.tsx), which owns the reverse conversion at
// the same point it already maps the rest of this form's data to the API
// segment shape.
export const millimetersToMeters = (mm: number | null): number | null =>
  mm == null ? null : mm / MILLIMETERS_PER_METER;

export const metersToMillimeters = (m: number | null): number | null =>
  m == null ? null : Math.round(m * MILLIMETERS_PER_METER);

const toFormBody = (borderData: BorderConfigurationData, i18n: Translation): BorderManualMeasurementsFormBody => {
  const borderOrientations = getBaseOrientations(i18n);
  const cornerClassifications = getCornerClassifications(i18n);

  return {
    isClosedPerimeter: borderData.isClosedPerimeter ?? false,
    segments:
      borderData.segments && borderData.segments.length > 0
        ? borderData.segments.map((segment) => ({
            length: millimetersToMeters(segment.length ?? null),
            orientation: borderOrientations.find((x) => x.value === segment.orientation) ?? null,
            cornerClassification: cornerClassifications.find((x) => x.value === segment.cornerClassification) ?? null,
          }))
        : [{ length: null, orientation: null, cornerClassification: null }],
  };
};

type BorderManualMeasurementsStepProps = {
  borderData: BorderConfigurationData;
  needsOrientation: boolean;
  isCornerOrientationRequired: boolean;
  canEdit?: boolean;
  isAdmin?: boolean;
  isLoading?: boolean;
  next: () => void;
  back: () => void;
  onSubmit: (body: BorderManualMeasurementsFormBody) => Promise<void> | undefined;
};

const BorderManualMeasurementsStep: FC<BorderManualMeasurementsStepProps> = ({
  borderData,
  needsOrientation,
  isCornerOrientationRequired,
  canEdit,
  isAdmin,
  isLoading,
  next,
  back,
  onSubmit,
}) => {
  const i18n = useI18n();
  const borderOrientations = getBaseOrientations(i18n.translation);
  const cornerClassifications = getCornerClassifications(i18n.translation);
  const defaultValues = toFormBody(borderData, i18n.translation);
  // This component only ever renders for reclining uprights (BorderPerimeterStep.tsx renders it
  // exclusively when isBorderUprightReclinable(uprightTypeCode) is true). Confirmed business
  // rule: reclining-upright internal corners always need FL/FR bases regardless of span, so span
  // is deliberately not part of this condition (mirrors the backend's
  // isBorderReclinableCornerOrientationApplicable gate).
  const cornerClassificationApplicable = isCornerOrientationRequired;

  return (
    <Form<BorderManualMeasurementsFormBody>
      mode="onBlur"
      defaultValues={defaultValues}
      disabled={isLoading || !canEdit}
      onSubmit={() => async (formData) =>
        isAdmin ? await onSubmit(formData) : canEdit ? await onSubmit(formData)?.then(() => next()) : next()
      }
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
      secondaryButton={
        <>
          {isLoading && <LoadingSpinner delayed={0} size="small" />}
          <Button
            buttonProps={{
              disabled: isLoading,
              onClick: back,
            }}
            type="secondary"
          >
            {i18n.translation.common.back}
          </Button>
        </>
      }
    >
      {({ control, setValue, reset, watch }) => {
        const { fields, append, remove } = useFieldArray({ control, name: "segments" });
        // isClosedPerimeter must be read live (not from borderData/defaultValues) so the
        // last-row corner-classification exemption below reacts to the switch being
        // toggled mid-session, not just its initially loaded value.
        const isClosedPerimeterWatched = !!watch("isClosedPerimeter");
        const isPerimeterEffectivelyClosed = isClosedPerimeterWatched && fields.length >= CLOSED_PERIMETER_MIN_SEGMENTS;

        useEffect(() => {
          reset(defaultValues);
        }, [borderData]);

        useEffect(() => {
          if (fields.length < CLOSED_PERIMETER_MIN_SEGMENTS && isClosedPerimeterWatched) {
            setValue("isClosedPerimeter", false);
          }
        }, [fields.length, isClosedPerimeterWatched]);

        const disabled = isLoading || !canEdit;

        return (
          <div className={styles.form}>
            {fields.length >= CLOSED_PERIMETER_MIN_SEGMENTS && (
              <Controller
                name="isClosedPerimeter"
                control={control}
                render={({ field }) => (
                  <FormField label={i18n.translation.common.closedPerimeter} isOptional>
                    {({ labelId }) => (
                      <Switch
                        id={labelId}
                        checked={!!field.value}
                        disabled={disabled}
                        onChange={(checked) => setValue("isClosedPerimeter", checked)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}

            <div className={styles.segmentRows}>
              {fields.map((field, index) => {
                const isLast = index === fields.length - 1;
                const canRemove = fields.length > 1;

                return (
                  <div key={field.id} className={styles.segmentRow}>
                    <div className={styles.fields}>
                      <Controller
                        name={`segments.${index}.length` as const}
                        control={control}
                        rules={{ required: true, validate: (value) => (value ?? 0) > 0 }}
                        render={({ field, fieldState }) => (
                          <FormField label={i18n.translation.common.length} error={fieldState.error}>
                            {({ labelId, isInvalid }) => (
                              <Numeric
                                {...field}
                                id={labelId}
                                isInvalid={isInvalid}
                                decimalScale={2}
                                allowNegative={false}
                                suffix=" m"
                                disabled={disabled}
                                onBlur={(value) =>
                                  setValue(`segments.${index}.length` as const, value, { shouldValidate: true })
                                }
                              />
                            )}
                          </FormField>
                        )}
                      />

                      {needsOrientation && (
                        <Controller
                          name={`segments.${index}.orientation` as const}
                          control={control}
                          rules={{ required: true }}
                          render={({ field, fieldState }) => (
                            <FormField label={i18n.translation.common.baseOrientation} error={fieldState.error}>
                              {({ labelId }) => (
                                <CustomSelect<BaseOrientation>
                                  {...field}
                                  inputId={labelId}
                                  options={borderOrientations}
                                  isDisabled={disabled}
                                  placeholder={`${i18n.translation.common.select}...`}
                                  getOptionLabel={(x) => x.label}
                                  getOptionValue={(x) => x.value}
                                  isInvalid={fieldState.invalid}
                                  onChange={(value) =>
                                    setValue(`segments.${index}.orientation` as const, value ?? null, {
                                      shouldValidate: true,
                                    })
                                  }
                                />
                              )}
                            </FormField>
                          )}
                        />
                      )}

                      {cornerClassificationApplicable && !(isLast && !isPerimeterEffectivelyClosed) && (
                        <Controller
                          name={`segments.${index}.cornerClassification` as const}
                          control={control}
                          rules={{ required: true }}
                          render={({ field, fieldState }) => (
                            <FormField label={i18n.translation.common.cornerClassification} error={fieldState.error}>
                              {({ labelId }) => (
                                <CustomSelect<CornerClassification>
                                  {...field}
                                  inputId={labelId}
                                  options={cornerClassifications}
                                  isDisabled={disabled}
                                  placeholder={`${i18n.translation.common.select}...`}
                                  getOptionLabel={(x) => x.label}
                                  getOptionValue={(x) => x.value}
                                  isInvalid={fieldState.invalid}
                                  onChange={(value) =>
                                    setValue(`segments.${index}.cornerClassification` as const, value ?? null, {
                                      shouldValidate: true,
                                    })
                                  }
                                />
                              )}
                            </FormField>
                          )}
                        />
                      )}
                    </div>

                    <div className={styles.actions}>
                      <Button
                        glyph={SvgAdd}
                        buttonProps={{
                          className: `${styles.rowAction} ${!isLast ? styles.hiddenAction : ""}`,
                          disabled: disabled || !isLast,
                          title: i18n.translation.common.add,
                          onClick: () => append({ length: null, orientation: null, cornerClassification: null }),
                        }}
                      />
                      {canRemove && (
                        <Button
                          glyph={SvgDelete}
                          type="secondary"
                          buttonProps={{
                            className: styles.rowAction,
                            disabled: disabled,
                            title: i18n.translation.common.delete,
                            onClick: () => remove(index),
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    </Form>
  );
};

export default BorderManualMeasurementsStep;
